/**
 * Shared “Squares game open” signal for Matches / homepage / challenge cards.
 * Same rules as Squares lobby CTA: live (or post-toss) phase + a playable 410 over.
 * Without stream markets → not open (GAME NOT OPEN YET).
 */

import {
  eventStatusToNumber,
  IN_PLAY_STATUS_IDS,
} from "../../Trader/Cricket/decimalEventStatus";
import { commentaryIndicatesTossWon } from "../shared/squaresCommentaryToss";
import {
  isWithinSquaresPlayOpenCountdown,
  scorecardTossConfirmed,
} from "../shared/squaresPlayOpenWindow";
import {
  getPlayableSquaresOverFromMarkets,
} from "../shared/squaresOverOdds";
import {
  cricketCurrentOver1BasedFromScoreboard,
  cricketInningsOversDecimalFromScoreboard,
  maxOversFromFixtureFormat,
  maxOversFromStreamEvent,
} from "../../Cricket/cricketMatchCardOverDisplay";
import { isSquaresSupportedCricketFormat, listNormalizedMatchStatusForSquares } from "./squaresLobbyMatchUtils";
import { cricketStreamIndicatesInningsBreak } from "../../Games/matchUtils";

function isStreamLiveEventStatus(status) {
  const n = eventStatusToNumber(status);
  return n != null && IN_PLAY_STATUS_IDS.includes(n);
}

function streamEventStatus(streamData) {
  const mu = streamData?.market_update;
  if (!mu) return null;
  if (mu.event_status != null && mu.event_status !== "") return mu.event_status;
  return mu.market_update?.event_status ?? null;
}

/**
 * True once Squares treats the match as past pure pretoss (toss confirmed and/or in-play).
 * @param {object} fixture
 * @param {object | undefined} streamData
 */
export function isSquaresLivePhaseForGameOpen(fixture, streamData) {
  if (!fixture) return false;
  const fixtureStatusUpper = String(fixture.status || "").toUpperCase();
  const matchStatus = listNormalizedMatchStatusForSquares(fixture);
  const upper = (matchStatus || "").toUpperCase();
  if (
    upper.startsWith("CANCELLED") ||
    upper.startsWith("MATCH ABANDONED") ||
    upper.startsWith("MATCH COMPLETE") ||
    upper.startsWith("EVENT CLOSED")
  ) {
    return false;
  }

  const isInPlay = matchStatus === "IN-PLAY";
  const isTossPendingList =
    fixtureStatusUpper.startsWith("TOSS PENDING") ||
    fixtureStatusUpper.startsWith("TOSS DELAYED") ||
    fixtureStatusUpper.includes("TOSS PENDING") ||
    fixtureStatusUpper.includes("TOSS DELAYED");

  const tossKnownFromCommentary = commentaryIndicatesTossWon(streamData?.commentary);
  const scorecardToss = scorecardTossConfirmed(streamData?.scorecard?.toss);
  const tossAllowsSquaresPlay = isTossPendingList
    ? tossKnownFromCommentary
    : tossKnownFromCommentary || scorecardToss;

  const es = streamEventStatus(streamData);
  const isStreamInPlayPhase = isStreamLiveEventStatus(es);
  const withinOpenCountdown = isWithinSquaresPlayOpenCountdown(fixture?.start);

  return (
    isInPlay ||
    matchStatus === "BREAK IN PLAY" ||
    isStreamInPlayPhase ||
    tossAllowsSquaresPlay ||
    withinOpenCountdown
  );
}

/**
 * 1-based over Squares would advertise on the lobby card, or null when GAME NOT OPEN YET.
 * @param {object} fixture
 * @param {object | undefined} streamData
 * @returns {number | null}
 */
export function getSquaresLobbyPlayOverNumber(fixture, streamData) {
  if (!isSquaresLivePhaseForGameOpen(fixture, streamData)) return null;

  const lobbyInningsMaxOvers =
    maxOversFromStreamEvent(streamData) ?? maxOversFromFixtureFormat(fixture?.format);

  const eventObj = streamData?.event?.event ?? streamData?.event;
  const rawCurrent = cricketCurrentOver1BasedFromScoreboard(eventObj?.scoreboard);
  let cricketCurrentOver = rawCurrent;
  if (
    cricketCurrentOver != null &&
    lobbyInningsMaxOvers != null &&
    Number.isFinite(lobbyInningsMaxOvers) &&
    lobbyInningsMaxOvers > 0
  ) {
    cricketCurrentOver = Math.min(cricketCurrentOver, lobbyInningsMaxOvers);
  }

  const inningsOversDec = cricketInningsOversDecimalFromScoreboard(eventObj?.scoreboard);
  const inningsBreak = cricketStreamIndicatesInningsBreak(streamData);
  if (
    inningsBreak ||
    (inningsOversDec != null &&
      lobbyInningsMaxOvers != null &&
      Number.isFinite(lobbyInningsMaxOvers) &&
      lobbyInningsMaxOvers > 0 &&
      inningsOversDec >= lobbyInningsMaxOvers)
  ) {
    // Completed innings: open Over 1 of the chase. Do not advertise leftover
    // inns-1 410s (e.g. Over 5 after all-out) while Decimal still offers them.
    return 1;
  }

  const mu = streamData?.market_update;
  const markets = mu?.markets ?? mu?.market_update?.markets;
  const fromMarkets = getPlayableSquaresOverFromMarkets(markets, cricketCurrentOver);
  let n = fromMarkets ?? null;
  if (
    n != null &&
    lobbyInningsMaxOvers != null &&
    Number.isFinite(lobbyInningsMaxOvers) &&
    lobbyInningsMaxOvers > 0
  ) {
    n = Math.min(Number(n), lobbyInningsMaxOvers);
  }

  return n != null && Number.isFinite(Number(n)) ? Number(n) : null;
}

/**
 * True when the Squares lobby would show PLAY OVER N (not GAME NOT OPEN YET).
 * Needs 410 markets — use on Squares lobby / match-detail streams, not trader-lobby listings.
 * @param {object} fixture
 * @param {object | undefined} streamData
 */
export function isSquaresGameOpenFromStream(fixture, streamData) {
  if (!isSquaresSupportedCricketFormat(fixture, streamData)) return false;
  return getSquaresLobbyPlayOverNumber(fixture, streamData) != null;
}
